import axios from "axios";
import { useRecoilState } from "recoil";
import { userAtom } from "./atoms/user";
import { User, UserRole } from "./Domain";
import { logoutAndRedirect } from "./logout";
import store from "./store";

const saveUser = (user: User | null) => {
	if (!user) {
		sessionStorage.removeItem("user");
		return;
	}

	sessionStorage["user"] = JSON.stringify(user);
};

// Applies the current user to whichever component last rendered useUser().
// Recoil setters are stable/shared for a given atom, so it's safe to call
// the most recently captured one from outside of React (e.g. from the axios
// interceptors below).
let applyUser: ((user: User | null) => void) | null = null;

const logout = () => {
	axios.defaults.headers.common["Authorization"] = undefined;

	saveUser(null);
	applyUser?.(null);

	logoutAndRedirect();
};

// These interceptors must be registered exactly once for the lifetime of the
// page. Previously they were (re-)registered on every setUser() call, and the
// success handler itself called setUser() again on every token rotation -
// each call adding another interceptor. Since every existing interceptor
// fires on every response, that meant the interceptor count (and therefore
// the work done per API call) doubled on every token-rotating response,
// eventually degrading the tab and producing exactly the kind of
// too-many-requests/unresponsive symptoms reported for long scan sessions.
let interceptorsRegistered = false;

const registerInterceptorsOnce = () => {
	if (interceptorsRegistered) {
		return;
	}
	interceptorsRegistered = true;

	axios.interceptors.response.use(
		(response) => {
			const newAccessToken = response.headers["access-token"];
			if (Boolean(newAccessToken)) {
				axios.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;

				const json = sessionStorage["user"];
				if (json) {
					const storedUser = JSON.parse(json);
					storedUser.loginToken.token = newAccessToken;
					saveUser(storedUser);
					applyUser?.(storedUser);
				}
			}
			return response;
		},
		(error) => {
			if (error.response && error.response.status === 401) {
				// noinspection JSIgnoredPromiseFromCall
				logout();
				return;
			}
			store.dispatch({ type: "SET_ERROR", error });
			return Promise.reject(error);
		},
	);
};

export default () => {
	const [user, _setUser] = useRecoilState<User>(userAtom);
	applyUser = _setUser;

	const hasRole = (role: UserRole) => user?.roles.some((r) => r.includes(role)) ?? false;
	const hasRoles = (roles: UserRole[]) => roles.every(hasRole);
	const hasAnyRole = (roles: UserRole[]) => roles.some(hasRole);

	const setUser = (newUser: User | null) => {
		registerInterceptorsOnce();

		if (newUser) {
			axios.defaults.headers.common["Authorization"] = "Bearer " + newUser.loginToken!.token;

			saveUser(newUser);
			_setUser(newUser);
		} else {
			logout();
		}
	};

	const loadUser = () => {
		const json = sessionStorage["user"];
		if (!!json) {
			const user = JSON.parse(json);
			if (!!user) {
				setUser(user);
			}
		}
	};

	if (!user) {
		loadUser();
	}

	return [user, setUser, hasRole, hasRoles, hasAnyRole] as const;
};
